import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import i18n from 'i18next'
import { G } from './theme'

/**
 * 날짜 고르기 — 브라우저 기본 달력(input type=date)을 쓰지 않는다(사용자 지시 2026-10-19).
 *   gold 가입 창 생년월일(v-calendar 팝오버)처럼 칸을 누르면 아래에 달력이 뜬다.
 *   제목(「10月 2026」)을 누르면 월 → 연(12년씩) 보기로 올라가 먼 해도 빨리 고른다.
 *   값 형식 'YYYY-MM-DD', 칸에는 'YYYY/MM/DD'. min/max 밖의 날은 고를 수 없다.
 *   달력은 body 에 붙여(portal) 가입 창 안쪽 스크롤에 잘리지 않게 하고, 아래 공간이 모자라면 위로 띄운다.
 *   가입 창이 스크롤되면 칸을 따라간다(칸이 화면 밖으로 나가면 닫힘).
 * @param {{ value: string, onChange: (v: string) => void, min?: string, max?: string, error?: boolean, inputStyle?: object }} props
 */
const pad = (n) => String(n).padStart(2, '0')
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const LABELS = {
  ja: { dow: ['日', '月', '火', '水', '木', '金', '土'], month: (m) => `${m + 1}月`, header: (y, m) => `${y}年${m + 1}月` },
  ko: { dow: ['일', '월', '화', '수', '목', '금', '토'], month: (m) => `${m + 1}월`, header: (y, m) => `${y}년 ${m + 1}월` },
  zh: { dow: ['日', '一', '二', '三', '四', '五', '六'], month: (m) => `${m + 1}月`, header: (y, m) => `${y}年${m + 1}月` },
  en: { dow: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'], month: (m) => MONTHS_EN[m], header: (y, m) => `${MONTHS_EN[m]} ${y}` },
  th: { dow: ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'], month: (m) => MONTHS_TH[m], header: (y, m) => `${MONTHS_TH[m]} ${y}` },
  vi: { dow: ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'], month: (m) => `Th${m + 1}`, header: (y, m) => `Tháng ${m + 1} ${y}` },
}

function parse(v) {
  const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(String(v || '').trim())
  if (!m) return null
  const p = { y: +m[1], mo: +m[2] - 1, d: +m[3] }
  const dt = new Date(Date.UTC(p.y, p.mo, p.d))
  return dt.getUTCMonth() === p.mo ? p : null
}
const ymd = (p) => `${p.y}-${pad(p.mo + 1)}-${pad(p.d)}`
const show = (v) => { const p = parse(v); return p ? `${p.y}/${pad(p.mo + 1)}/${pad(p.d)}` : '' }

export default function GoldDatePicker({ value, onChange, min, max, error = false, inputStyle }) {
  const L = LABELS[(i18n.language || 'ja').slice(0, 2)] || LABELS.ja
  const boxRef = useRef(null)
  const popRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState('days')
  const [cursor, setCursor] = useState({ y: 2000, mo: 0 })
  const [pos, setPos] = useState(null)

  const sel = parse(value)
  const outOfRange = (ds) => (max && ds > max) || (min && ds < min)

  const openPicker = () => {
    const base = sel || parse(max) || parse(ymd({ y: new Date().getFullYear(), mo: new Date().getMonth(), d: 1 }))
    setCursor({ y: base.y, mo: base.mo })
    setView('days')
    setOpen((v) => !v)
  }

  // 칸 아래(공간이 모자라면 위)에 띄운다. 칸이 화면 밖으로 나가면 닫는다
  const place = () => {
    const r = boxRef.current?.getBoundingClientRect()
    if (!r) return
    if (r.bottom < 0 || r.top > window.innerHeight) { setOpen(false); return }
    const H = 300
    const up = window.innerHeight - r.bottom < H + 8 && r.top > window.innerHeight - r.bottom
    setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - 284)), top: up ? undefined : r.bottom + 6, bottom: up ? window.innerHeight - r.top + 6 : undefined })
  }

  useLayoutEffect(() => {
    if (open) place()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (boxRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return
      setOpen(false)
    }
    // 칸을 품은 스크롤(가입 창·페이지)만 따라간다 — 공지 흐름 글처럼 다른 곳 스크롤은 무시
    const onScroll = (e) => {
      const t = e.target
      if (popRef.current?.contains(t)) return
      if (t === document || t === document.documentElement || (t instanceof Node && t.contains(boxRef.current))) place()
    }
    const onResize = () => place()
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown, { passive: true })
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const { y, mo } = cursor
  const maxMonth = max ? max.slice(0, 7) : null
  const minMonth = min ? min.slice(0, 7) : null
  const monthKey = (yy, mm) => `${yy}-${pad(mm + 1)}`
  const maxY = max ? +max.slice(0, 4) : null
  const minY = min ? +min.slice(0, 4) : null

  const days = () => {
    const first = new Date(Date.UTC(y, mo, 1))
    const start = new Date(Date.UTC(y, mo, 1 - first.getUTCDay()))
    const cells = []
    for (let i = 0; i < 42; i++) {
      const d = new Date(start.getTime() + i * 86400000)
      const p = { y: d.getUTCFullYear(), mo: d.getUTCMonth(), d: d.getUTCDate() }
      const ds = ymd(p)
      cells.push({ p, ds, other: p.mo !== mo, disabled: outOfRange(ds), active: sel && ymd(sel) === ds })
    }
    const prevKey = monthKey(mo === 0 ? y - 1 : y, mo === 0 ? 11 : mo - 1)
    const nextKey = monthKey(mo === 11 ? y + 1 : y, mo === 11 ? 0 : mo + 1)
    const go = (delta) => { const t = new Date(Date.UTC(y, mo + delta, 1)); setCursor({ y: t.getUTCFullYear(), mo: t.getUTCMonth() }) }
    return (
      <>
        <Head>
          <Arrow type="button" disabled={!!minMonth && prevKey < minMonth} onClick={() => go(-1)} aria-label="previous">‹</Arrow>
          <Title type="button" onClick={() => setView('months')}>{L.header(y, mo)}</Title>
          <Arrow type="button" disabled={!!maxMonth && nextKey > maxMonth} onClick={() => go(1)} aria-label="next">›</Arrow>
        </Head>
        <Grid $cols={7}>
          {L.dow.map((d) => <Dow key={d}>{d}</Dow>)}
          {cells.map((c) => (
            <Day
              key={c.ds}
              type="button"
              disabled={c.disabled}
              $other={c.other}
              $active={c.active}
              onClick={() => { onChange(c.ds); setOpen(false) }}
            >
              {c.p.d}
            </Day>
          ))}
        </Grid>
      </>
    )
  }

  const months = () => (
    <>
      <Head>
        <Arrow type="button" disabled={!!minY && y - 1 < minY} onClick={() => setCursor({ y: y - 1, mo })} aria-label="previous">‹</Arrow>
        <Title type="button" onClick={() => setView('years')}>{y}</Title>
        <Arrow type="button" disabled={!!maxY && y + 1 > maxY} onClick={() => setCursor({ y: y + 1, mo })} aria-label="next">›</Arrow>
      </Head>
      <Grid $cols={3}>
        {Array.from({ length: 12 }, (_, m) => {
          const key = monthKey(y, m)
          const disabled = (!!maxMonth && key > maxMonth) || (!!minMonth && key < minMonth)
          return (
            <Cell key={m} type="button" disabled={disabled} $active={!!sel && sel.y === y && sel.mo === m} onClick={() => { setCursor({ y, mo: m }); setView('days') }}>
              {L.month(m)}
            </Cell>
          )
        })}
      </Grid>
    </>
  )

  const years = () => {
    const start = Math.floor(y / 12) * 12
    return (
      <>
        <Head>
          <Arrow type="button" disabled={!!minY && start - 1 < minY} onClick={() => setCursor({ y: y - 12, mo })} aria-label="previous">‹</Arrow>
          <Title as="span">{start} - {start + 11}</Title>
          <Arrow type="button" disabled={!!maxY && start + 12 > maxY} onClick={() => setCursor({ y: y + 12, mo })} aria-label="next">›</Arrow>
        </Head>
        <Grid $cols={3}>
          {Array.from({ length: 12 }, (_, i) => {
            const yy = start + i
            const disabled = (!!maxY && yy > maxY) || (!!minY && yy < minY)
            return (
              <Cell key={yy} type="button" disabled={disabled} $active={!!sel && sel.y === yy} onClick={() => { setCursor({ y: yy, mo }); setView('months') }}>
                {yy}
              </Cell>
            )
          })}
        </Grid>
      </>
    )
  }

  return (
    <>
      <Box ref={boxRef} type="button" $error={error} style={inputStyle} onClick={openPicker}>
        {show(value)}
      </Box>
      {open && pos && createPortal(
        <Pop ref={popRef} style={{ left: pos.left, top: pos.top, bottom: pos.bottom }} onMouseDown={(e) => e.preventDefault()}>
          {view === 'days' && days()}
          {view === 'months' && months()}
          {view === 'years' && years()}
        </Pop>,
        document.body,
      )}
    </>
  )
}

const ERROR_RED = '#ff4d5e'

const Box = styled.button`
  && {
    display: block;
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 0;
    border-radius: 4px;
    background: ${G.bg};
    font-size: 14px;
    line-height: 40px; /* 사이트 공통 button { line-height: 0 } 대신 */
    color: ${G.sand};
    text-align: left;
    cursor: pointer;
    outline: none;
    box-shadow: ${({ $error }) => ($error ? `inset 0 0 0 1px ${ERROR_RED}` : 'none')};
  }
  &:focus-visible { box-shadow: inset 0 0 0 1px ${G.gold}; }
`

/* v-calendar 어두운 팝오버와 같은 색(gold 가입 창 생년월일) */
const Pop = styled.div`
  position: fixed;
  z-index: 3000;
  width: 276px;
  padding: 8px 10px 10px;
  border: 1px solid #4a5568;
  border-radius: 8px;
  background: #2d3748;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  color: #fff;
  user-select: none;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`

const Arrow = styled.button`
  && {
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-size: 22px;
    line-height: 26px;
    color: #fff;
    cursor: pointer;
  }
  &&:hover:not(:disabled) { background: #4a5568; }
  &&:disabled { opacity: 0.3; cursor: default; }
`

const Title = styled.button`
  && {
    padding: 4px 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    color: #fff;
    cursor: pointer;
  }
  &&:hover { background: #4a5568; }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
  gap: ${({ $cols }) => ($cols === 7 ? '2px' : '6px')};
`

const Dow = styled.span`
  padding: 4px 0;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #a0aec0;
`

const Day = styled.button`
  && {
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    line-height: 32px;
    background: ${({ $active }) => ($active ? G.gold : 'transparent')};
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 700 : 400)};
    color: ${({ $active, $other }) => ($active ? '#000' : $other ? '#718096' : '#fff')};
    cursor: pointer;
  }
  &&:hover:not(:disabled) { background: ${({ $active }) => ($active ? G.gold : '#4a5568')}; }
  &&:disabled { opacity: 0.3; cursor: default; }
`

const Cell = styled.button`
  && {
    height: 40px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    line-height: 40px;
    background: ${({ $active }) => ($active ? G.gold : 'transparent')};
    font-size: 14px;
    color: ${({ $active }) => ($active ? '#000' : '#fff')};
    cursor: pointer;
  }
  &&:hover:not(:disabled) { background: ${({ $active }) => ($active ? G.gold : '#4a5568')}; }
  &&:disabled { opacity: 0.3; cursor: default; }
`
